import {Alert, Button, Col, Row, Space, Spin, message} from "antd";
import {useNavigate} from "react-router-dom";
import {yupResolver} from "@hookform/resolvers/yup";
import {FormProvider, useForm} from "react-hook-form";
import {useGetProductContext} from "../context/get-product";
import {useDeleteProductHook} from "../hooks/delete-product";
import {StyledServiceButtonWrapper} from "../../../../styles/onboarding/onboarding";
import {ServiceFormLayout} from "../../../../calls/useCases/createService/service-form-layout";
import {OfferingDeleteButton} from "../../../../components/offering-form/general/delete-button";
import {ProductVariantsFormValues} from "../../../../components/offering-form/products/variants/types";
import {variantFormValidationSchema} from "../../../../components/offering-form/products/variants/schema";
import {ProductVariantFormFields} from "../../../../components/offering-form/products/variants";
import {OfferingFormPriceType} from "@/features/pricing";
import {AddOn, useDigitalProductAddOns} from "../../../../features/digital-products";
import {IS_PWYW_ENABLED} from "../../../../services/config";
import {readDigitalProductPriceType} from "../../product-pricing/read-price-type";
import {addOnsToFormValues} from "../../add-ons/add-ons-form";
import {useSaveAddOns} from "../../add-ons/use-save-add-ons";

type EditVariantsFormInnerProps = {
  productId: number;
  addOns: AddOn[];
  hideVariantFromProfile: boolean;
  productCurrencies: {callPrice: number; callPriceCurrency: string}[];
  productSelectedPriceType: OfferingFormPriceType;
};

function EditVariantsFormInner({
  productId,
  addOns,
  hideVariantFromProfile,
  productCurrencies,
  productSelectedPriceType,
}: EditVariantsFormInnerProps) {
  const navigate = useNavigate();
  const {deleteProduct} = useDeleteProductHook(productId);
  const {saveAddOns, isSaving} = useSaveAddOns(productId);

  const variantFormMethods = useForm<ProductVariantsFormValues>({
    resolver: yupResolver(variantFormValidationSchema),
    mode: "onChange",
    defaultValues: addOnsToFormValues(addOns, !hideVariantFromProfile),
  });

  const isFormError = Object.keys(variantFormMethods.formState.errors).length > 0;
  const isVariantError = variantFormMethods.formState.errors.variants;

  async function onSubmit(data: ProductVariantsFormValues) {
    try {
      // Fans out to DELETE(s) → PUT (existing groups) → POST (new groups).
      await saveAddOns(addOns, data);
      message.success("Updated Successfully");
      navigate("/products");
    } catch (error) {
      const apiError = error as {
        response?: {data?: {message?: string}};
        message?: string;
      };
      message.error(
        apiError.response?.data?.message ||
          apiError.message ||
          "Something went wrong updating your add-ons"
      );
    }
  }

  return (
    <ServiceFormLayout>
      <FormProvider {...variantFormMethods}>
        <form onSubmit={variantFormMethods.handleSubmit(onSubmit)}>
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <ProductVariantFormFields
              isVariantError={!!isVariantError}
              productCurrencies={productCurrencies}
              productSelectedPriceType={productSelectedPriceType}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}

            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    onClick={() => navigate("/products")}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    disabled={
                      Object.keys(variantFormMethods.formState.errors).length > 0 ||
                      Object.values(variantFormMethods.watch()).length < 1
                    }
                    loading={isSaving}
                  >
                    Update product
                  </Button>
                  <OfferingDeleteButton
                    cancelText="Exit"
                    content="Are you sure you want to delete this product? This action cannot be undone."
                    okText="Delete product"
                    onOk={deleteProduct}
                    title="Confirm product deletion"
                  />
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
    </ServiceFormLayout>
  );
}

function EditVariantsForm() {
  const {
    state: {productData},
  } = useGetProductContext();
  const productId = productData.product.id;

  // Add-ons are fetched on their own (v2 has a dedicated endpoint; v1 reads
  // them from the product). Gate the form on this load so React Hook Form
  // captures correct default values on mount.
  const addOnsQuery = useDigitalProductAddOns(productId);

  if (addOnsQuery.isLoading) {
    return (
      <ServiceFormLayout>
        <div style={{display: "flex", justifyContent: "center", padding: "48px 0"}}>
          <Spin />
        </div>
      </ServiceFormLayout>
    );
  }

  if (addOnsQuery.isError) {
    return (
      <ServiceFormLayout>
        <Alert
          type="error"
          message="Could not load add-ons"
          description="Please try again."
          action={
            <Button size="small" onClick={() => addOnsQuery.refetch()}>
              Retry
            </Button>
          }
        />
      </ServiceFormLayout>
    );
  }

  return (
    <EditVariantsFormInner
      productId={productId}
      addOns={addOnsQuery.data ?? []}
      hideVariantFromProfile={productData.product.hideVariantFromProfile}
      productCurrencies={productData.currencies.map((c) => ({
        callPrice: c.amount,
        callPriceCurrency: c.currency,
      }))}
      productSelectedPriceType={readDigitalProductPriceType(productData, IS_PWYW_ENABLED)}
    />
  );
}

export default EditVariantsForm;
